import { useMemo } from 'react'
import { useAuth } from '@/contexts/AuthProvider'
import { useMobileProfile } from '@/hooks/useMobileProfile'
import QualificationCard from '@/components/QualificationCard'
import CheckinStatusCard from '@/components/CheckinStatusCard'
import { Loader2, ShieldCheck, AlertTriangle, XCircle, TrendingUp } from 'lucide-react'
import { getDisplayStatus } from '@/lib/qualificationStatus'

export default function HomePage() {
  const { userProfile, company } = useAuth()
  const { qualifications, recentCheckins, complianceStats, loading, refetch } = useMobileProfile(userProfile?.id, company?.id)

  const urgent = useMemo(() => {
    const expiring = qualifications.filter(q => getDisplayStatus(q) === 'expiring_soon').slice(0, 3)
    const expired = qualifications.filter(q => getDisplayStatus(q) === 'expired').slice(0, 3)
    return [...expired, ...expiring].slice(0, 3)
  }, [qualifications])

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="h-6 w-6 animate-spin text-emerald-500" />
      </div>
    )
  }

  return (
    <div className="p-4 space-y-5">
      <div>
        <h1 className="text-xl font-bold text-gray-900">
          Hi, {userProfile?.first_name || 'there'}
        </h1>
        <p className="text-sm text-gray-500">{company?.name}</p>
      </div>

      {/* Compliance Overview */}
      <div className="grid grid-cols-2 gap-3">
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp className="h-4 w-4 text-emerald-500" />
            <span className="text-xs font-medium text-gray-500 uppercase">Compliance</span>
          </div>
          <p className="text-3xl font-bold text-gray-900">{complianceStats.compliance_percentage}%</p>
          <p className="text-xs text-gray-400 mt-1">{complianceStats.compliant} of {complianceStats.total} compliant</p>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center gap-2 mb-2">
            {complianceStats.expired > 0 ? (
              <XCircle className="h-4 w-4 text-red-500" />
            ) : complianceStats.expiring > 0 ? (
              <AlertTriangle className="h-4 w-4 text-amber-500" />
            ) : (
              <ShieldCheck className="h-4 w-4 text-emerald-500" />
            )}
            <span className="text-xs font-medium text-gray-500 uppercase">Attention</span>
          </div>
          {complianceStats.expired > 0 ? (
            <>
              <p className="text-3xl font-bold text-red-600">{complianceStats.expired}</p>
              <p className="text-xs text-gray-400 mt-1">expired items</p>
            </>
          ) : complianceStats.expiring > 0 ? (
            <>
              <p className="text-3xl font-bold text-amber-600">{complianceStats.expiring}</p>
              <p className="text-xs text-gray-400 mt-1">expiring soon</p>
            </>
          ) : (
            <>
              <p className="text-3xl font-bold text-emerald-600">0</p>
              <p className="text-xs text-gray-400 mt-1">all clear</p>
            </>
          )}
        </div>
      </div>

      {/* Clock Status */}
      <div>
        <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-2">Clock Status</h2>
        <CheckinStatusCard recentCheckins={recentCheckins} />
      </div>

      {/* Urgent Qualifications */}
      {urgent.length > 0 && (
        <div>
          <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-2">Needs Attention</h2>
          <div className="space-y-2">
            {urgent.map(q => (
              <QualificationCard key={q.id} qualification={q} />
            ))}
          </div>
        </div>
      )}

      {/* Recent Check-ins */}
      {recentCheckins.length > 0 && (
        <div>
          <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-2">Recent Check-ins</h2>
          <div className="bg-white rounded-xl border border-gray-200 divide-y divide-gray-100">
            {recentCheckins.slice(0, 5).map(c => (
              <div key={c.id} className="px-4 py-3 flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-900">{c.site_location?.name || 'Unknown'}</p>
                  <p className="text-xs text-gray-400">
                    {new Date(c.check_in_time).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                    {' '}
                    {new Date(c.check_in_time).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                  </p>
                </div>
                {c.check_out_time ? (
                  <span className="text-xs text-gray-400">
                    {new Date(c.check_out_time).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                  </span>
                ) : (
                  <span className="text-xs text-emerald-600 font-medium">Active</span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
